<template>
  <div v-if="!commentWasRemoved" class="sf-commcard-post" :data-commentid="id">
    <div class="sf-commcard-post-author">
      <div class="sf-commcard-post-name">{{ authorName }}</div>
      <div
        v-if="editedAt"
        v-tooltip.timestamp="editedAt"
        class="sf-commcard-post-date"
      >
        {{ I18n.t('communication.cards.edited') }}
        {{ editedAtFormatted }}
      </div>
      <div v-else v-tooltip.timestamp="createdAt" class="sf-commcard-post-date">
        {{ createdAtFormatted }}
      </div>
    </div>
    <CommentForm
      v-if="isEditing"
      textarea-class="sf-form-textarea sf-full"
      :class="hideSessionClass"
      v-bind="{
        id: id,
        text: text,
        commentableId: commentableId,
        commentableType: commentableType,
        attachmentIds: attachmentIds,
        ledgerId,
        companyId,
      }"
    ></CommentForm>
    <div
      v-else-if="!isEditing"
      v-dompurify-html:allowTarget="renderedText"
      class="sf-commcard-post-text"
      :class="hideSessionClass"
    ></div>
    <div v-if="attachments.length > 0 && !isEditing">
      <div class="sf-level sf-level-small sf-block">
        <div
          v-for="attachment in attachments"
          :key="attachment.id"
          class="sf-label sf-below-1"
        >
          <a
            :href="attachment.url"
            class="sf-a sf-level-item"
            :class="hideSessionClass"
            target="_blank"
            >{{ attachment.filename }}</a
          >
        </div>
      </div>
    </div>
    <div class="sf-commcard-post-actions">
      <a
        v-if="!isEditing && allowedActions.edit && !disableInteraction"
        href="#"
        @click.prevent="addEditCommentId(id)"
        >{{ I18n.t('common.edit') }}</a
      >
      <a
        v-if="allowedActions.remove && !disableInteraction"
        href="#"
        @click.prevent="handleRemoveClick"
        >{{ I18n.t('common.remove') }}</a
      >
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'

import CommentForm from './comment-form'
import communicationDate from '@app/utils/communication-date'
import truncate from '@app/utils/truncate'
import useRedactSessionRecording from '@app/shared/vue/composables/use-hide-session-recording'
import api from '@app/application/communication/sections/communication/api/comment'
import { SfLog } from '@app/adapters/logger'
import { isCurrentWorkflowLocked } from '@app/adapters/silverfin'

export default {
  components: {
    CommentForm,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    text: {
      type: String,
      required: true,
    },
    renderedText: {
      type: String,
      required: true,
    },
    authorId: {
      type: Number,
      required: true,
    },
    companyId: {
      type: Number,
      required: true,
    },
    ledgerId: {
      type: Number,
      required: true,
    },
    botName: {
      type: String,
      required: false,
    },
    createdAt: {
      type: String,
      required: true,
    },
    updatedAt: {
      type: String,
      required: true,
    },
    editedAt: {
      type: String,
    },
    attachmentIds: {
      type: Array,
      required: true,
    },
    allowedActions: {
      type: Object,
      required: true,
    },
    commentableId: {
      type: Number,
      required: true,
    },
    commentableType: {
      type: String,
      required: true,
    },
  },

  setup() {
    const hideSessionClass = useRedactSessionRecording()
    return {
      disableInteraction: isCurrentWorkflowLocked(),
      hideSessionClass,
    }
  },
  data: function () {
    return {
      commentWasRemoved: false,
    }
  },

  computed: {
    ...mapState({
      editCommentIds: (state) => state.ui.editCommentIds,
    }),

    ...mapGetters(['userById', 'attachmentArray']),

    createdByBot() {
      return !!this.botName
    },

    authorName() {
      if (this.createdByBot) {
        return this.botName
      } else {
        return this.userById(this.authorId).name
      }
    },

    attachments() {
      const contextualAttachments = this.attachmentArray.filter(
        (attachment) => attachment && this.attachmentIds.includes(attachment.id)
      )

      return contextualAttachments.map((attachment) => ({
        ...attachment,
        filename: truncate(attachment.filename),
      }))
    },

    createdAtFormatted() {
      return communicationDate(this.createdAt)
    },

    editedAtFormatted() {
      return communicationDate(this.editedAt)
    },

    isEditing() {
      return this.editCommentIds.includes(this.id)
    },
  },

  methods: {
    ...mapActions(['addEditCommentId']),
    handleRemoveClick() {
      window.sfConfirm({
        onConfirm: this.destroyComment,
      })
    },

    async destroyComment() {
      try {
        await api.destroyComment(
          this.id,
          this.companyId,
          this.commentableType,
          this.commentableId
        )

        this.commentWasRemoved = true
      } catch (e) {
        SfLog.warn('Error while removing comment', {
          extra: {
            error: e,
          },
        })
      }
    },
  },
}
</script>
